/**
 * Format Painter — visual-style copy/paste across editable elements.
 *
 * Captures both a section's per-element overrides (the explicit panel keys
 * like buttonType / buttonBgColor / fontSize) AND a computed-style snapshot
 * (VISUAL_CSS_PROPERTIES, read via getComputedStyle on the source element in
 * the iframe). The snapshot lands on the target as a `paintedStyles` nested
 * override; css-assembler emits its entries with `!important` so authored
 * section CSS (e.g. nth-child padding) loses to the painted value.
 *
 * Merge semantics — for each key in VISUAL_KEYS[type] (which includes
 * `paintedStyles`):
 *   - source has it    → target[key] = source[key]   (set / replace)
 *   - source missing   → delete target[key]          (clears any prior value)
 * Keys outside VISUAL_KEYS[type] (className, elementId, hidden, decorative
 * scroll attrs, etc.) are preserved on the target.
 *
 * State is held as a single in-memory record. Paint mode exits after a
 * successful apply OR via Esc / clicking the canvas. Mismatched editable
 * types show a toast and keep paint mode active so the user can pick a
 * valid target.
 *
 * Two-undo model: enterPaintMode and exitPaintMode never touch the store.
 * applyToTarget runs a single `store.batch(mutations, 'Paste style')` so
 * the painted target is one Undo away.
 */

import { store } from '../core/state.js';
import { eventBus } from '../core/events.js';
import { resolveSectionByInstance } from './section-utils.js';

// Computed-style property names captured via getComputedStyle on the source
// element. Longhand only — shorthand returns a serialization from longhands
// and would double-emit. Each list is intentionally curated per editable type;
// width/height/position are layout, not visual, and stay out.
export const VISUAL_CSS_PROPERTIES = {
  button: [
    'paddingTop',
    'paddingRight',
    'paddingBottom',
    'paddingLeft',
    'backgroundColor',
    'color',
    'borderTopWidth',
    'borderRightWidth',
    'borderBottomWidth',
    'borderLeftWidth',
    'borderTopStyle',
    'borderRightStyle',
    'borderBottomStyle',
    'borderLeftStyle',
    'borderTopColor',
    'borderRightColor',
    'borderBottomColor',
    'borderLeftColor',
    'borderTopLeftRadius',
    'borderTopRightRadius',
    'borderBottomLeftRadius',
    'borderBottomRightRadius',
    'boxShadow',
    'fontFamily',
    'fontSize',
    'fontWeight',
    'fontStyle',
    'letterSpacing',
    'lineHeight',
    'textTransform',
    'textDecorationLine',
    'textDecorationStyle',
    'textDecorationColor'
  ],
  image: [
    'borderTopLeftRadius',
    'borderTopRightRadius',
    'borderBottomLeftRadius',
    'borderBottomRightRadius',
    'boxShadow',
    'aspectRatio',
    'objectFit',
    'maxWidth',
    'maxHeight',
    'opacity',
    'filter'
  ],
  text: [
    'color',
    'fontFamily',
    'fontSize',
    'fontWeight',
    'fontStyle',
    'letterSpacing',
    'lineHeight',
    'textAlign',
    'textTransform',
    'textDecorationLine',
    'textDecorationStyle',
    'textDecorationColor'
  ],
  svg: ['fill', 'stroke', 'strokeWidth', 'opacity'],
  container: []
};

// Per-element override keys that participate in paint. `paintedStyles` is
// always present so the computed-style snapshot rides along. Source-missing
// + target-has on any of these keys deletes from the target during paint.
export const VISUAL_KEYS = {
  button: ['paintedStyles', 'buttonType', 'buttonBgColor', 'fontSize', 'borderRadius', 'boxShadow'],
  image: [
    'paintedStyles',
    'borderRadius',
    'boxShadow',
    'aspectRatio',
    'objectFit',
    'objectPosition',
    'imageTx',
    'imageTy',
    'imageScale',
    'imageHover',
    'imageEffect'
  ],
  text: ['paintedStyles', 'fontSize'],
  svg: ['paintedStyles', 'preserveAspectRatio'],
  container: ['paintedStyles']
};

let _state = null;
let _escListener = null;

eventBus.on('editable-ids:repaired', ({ instanceIds = [] } = {}) => {
  if (instanceIds.includes(_state?.sourceInstanceId)) exitPaintMode();
});

/**
 * Pure merge function — exposed for tests. Returns the next overrides entry
 * for the target element. Keys in `visualKeys` are replaced or deleted based
 * on the source; everything else on the target is preserved.
 */
export function paintTargetOverrides(sourceOverrides, targetOverrides, visualKeys) {
  const src = sourceOverrides || {};
  const next = { ...(targetOverrides || {}) };
  for (const k of visualKeys || []) {
    if (src[k] != null) {
      next[k] = _cloneValue(src[k]);
    } else {
      delete next[k];
    }
  }
  return next;
}

function _cloneValue(v) {
  if (v == null || typeof v !== 'object') return v;
  return JSON.parse(JSON.stringify(v));
}

function _cloneRect(rect) {
  if (!rect || typeof rect !== 'object') return null;
  const top = Number(rect.top);
  const left = Number(rect.left);
  const width = Number(rect.width);
  const height = Number(rect.height);
  if (![top, left, width, height].every(Number.isFinite)) return null;
  return { top, left, width, height };
}

/**
 * Capture the source's computed styles for the current editable type.
 * Returns a plain object keyed by camelCase JS property name (matching
 * VISUAL_CSS_PROPERTIES). Reads from the iframe document directly — same
 * origin (srcdoc/blob) so getComputedStyle is accessible from the parent.
 */
function captureComputedStyles(instanceId, eid, editableType) {
  const props = VISUAL_CSS_PROPERTIES[editableType] || [];
  if (props.length === 0) return {};

  const iframe = typeof document !== 'undefined' ? document.getElementById('pb-preview') : null;
  if (!iframe?.contentDocument || !iframe?.contentWindow) return {};

  const doc = iframe.contentDocument;
  const win = iframe.contentWindow;
  // CSS.escape may not exist in the iframe context — guard.
  const escape =
    typeof win.CSS !== 'undefined' && win.CSS.escape
      ? win.CSS.escape
      : (s) => String(s).replace(/(["\\\]])/g, '\\$1');

  const el =
    doc.querySelector(
      `[data-pb-instance="${escape(instanceId)}"] [data-pb-eid="${escape(eid)}"]`
    ) || doc.querySelector(`[data-pb-eid="${escape(eid)}"]`);
  if (!el) return {};

  const cs = win.getComputedStyle(el);
  const out = {};
  for (const prop of props) {
    const v = cs[prop];
    if (typeof v === 'string' && v !== '') out[prop] = v;
  }
  return out;
}

/**
 * Enter paint mode. Captures the source's per-element overrides AND its
 * computed styles. Both are stored in `_state` so the next valid target
 * click can apply them. Idempotent — subsequent calls replace the snapshot.
 */
export function enterPaintMode({
  instanceId,
  eid,
  editableType,
  rect = null,
  rectIsViewportRelative = false
}) {
  if (!instanceId || !eid) return false;
  const type = editableType || '';
  const resolved = resolveSectionByInstance(instanceId);
  if (!resolved?.section) return false;

  const srcOverrides = resolved.section?.overrides?.elements?.[eid] || {};
  const visualKeys = VISUAL_KEYS[type] || ['paintedStyles'];

  // Carry only the visual override keys forward (minus paintedStyles —
  // that's reconstructed below from getComputedStyle). Defensive copy.
  const carriedOverrides = {};
  for (const k of visualKeys) {
    if (k === 'paintedStyles') continue;
    if (srcOverrides[k] != null) carriedOverrides[k] = _cloneValue(srcOverrides[k]);
  }

  // Computed-style snapshot — values like '16px', 'rgb(255,255,255)'. Stored
  // as paintedStyles on the source-summary object that gets paint-merged
  // into the target. Fresh getComputedStyle wins when available; otherwise
  // fall back to whatever paintedStyles already lived on the source override
  // (covers re-paint cycles and JSDOM unit tests where no iframe exists).
  const paintedStyles = captureComputedStyles(instanceId, eid, type);
  if (paintedStyles && Object.keys(paintedStyles).length > 0) {
    carriedOverrides.paintedStyles = paintedStyles;
  } else if (srcOverrides.paintedStyles && typeof srcOverrides.paintedStyles === 'object') {
    carriedOverrides.paintedStyles = _cloneValue(srcOverrides.paintedStyles);
  }

  _state = {
    active: true,
    sourceInstanceId: instanceId,
    sourceEid: eid,
    sourceEditableType: type,
    sourceOverrides: carriedOverrides,
    sourceRect: _cloneRect(rect),
    sourceRectIsViewportRelative: !!rectIsViewportRelative
  };

  _attachEscListener();
  _setBodyPaintCursor(true);
  eventBus.emit('format-painter:entered', {
    instanceId,
    eid,
    editableType: type,
    rect: _cloneRect(_state.sourceRect),
    rectIsViewportRelative: !!_state.sourceRectIsViewportRelative
  });
  return true;
}

export function exitPaintMode() {
  if (!_state) return false;
  _state = null;
  _detachEscListener();
  _setBodyPaintCursor(false);
  eventBus.emit('format-painter:exited');
  return true;
}

export function isPaintModeActive() {
  return !!(_state && _state.active);
}

export function getPaintSource() {
  if (!_state) return null;
  return {
    instanceId: _state.sourceInstanceId,
    eid: _state.sourceEid,
    editableType: _state.sourceEditableType,
    rect: _cloneRect(_state.sourceRect),
    rectIsViewportRelative: !!_state.sourceRectIsViewportRelative
  };
}

/**
 * Apply the painted snapshot to a target element. On editable-type mismatch
 * stays in paint mode and returns `{ ok: false, reason: 'type-mismatch' }`
 * so the caller can show a toast. On success exits paint mode and returns
 * `{ ok: true, label: 'Paste style' }`.
 *
 * Same-source-as-target → no-op (returns ok: false, reason: 'same-element').
 *
 * Commits via `store.batch([{path, value}], 'Paste style')` — one undo step.
 */
export function applyToTarget({ instanceId, eid, editableType }) {
  if (!_state) return { ok: false, reason: 'not-active' };
  if (!instanceId || !eid) return { ok: false, reason: 'invalid-target' };

  const targetType = editableType || '';
  if (_state.sourceEditableType && _state.sourceEditableType !== targetType) {
    return {
      ok: false,
      reason: 'type-mismatch',
      sourceType: _state.sourceEditableType,
      targetType
    };
  }

  if (instanceId === _state.sourceInstanceId && eid === _state.sourceEid) {
    return { ok: false, reason: 'same-element' };
  }

  const resolved = resolveSectionByInstance(instanceId);
  if (!resolved?.section) return { ok: false, reason: 'no-target-section' };

  const visualKeys = VISUAL_KEYS[targetType] || ['paintedStyles'];

  const currentOverrides = resolved.section.overrides || {};
  const currentElements = currentOverrides.elements || {};
  const currentTargetEntry = currentElements[eid] || {};

  const nextTargetEntry = paintTargetOverrides(
    _state.sourceOverrides,
    currentTargetEntry,
    visualKeys
  );

  const nextElements = { ...currentElements };
  if (Object.keys(nextTargetEntry).length > 0) {
    nextElements[eid] = nextTargetEntry;
  } else {
    delete nextElements[eid];
  }

  const nextOverrides = { ...currentOverrides };
  if (Object.keys(nextElements).length > 0) {
    nextOverrides.elements = nextElements;
  } else {
    delete nextOverrides.elements;
  }

  const overridesPath = resolved.overridesPath || `${resolved.sectionPath}.overrides`;
  const mutations = [
    { path: overridesPath, value: Object.keys(nextOverrides).length > 0 ? nextOverrides : null }
  ];

  store.batch(mutations, 'Paste style');
  eventBus.emit('theme:override-changed');

  const source = getPaintSource();
  exitPaintMode();
  eventBus.emit('format-painter:applied', {
    source,
    target: { instanceId, eid, editableType: targetType }
  });
  return { ok: true };
}

function _attachEscListener() {
  if (_escListener || typeof window === 'undefined') return;
  _escListener = (e) => {
    if (!isPaintModeActive()) return;
    const key = (e.key || '').toLowerCase();
    if (key === 'escape' || key === 'esc') {
      e.preventDefault();
      e.stopPropagation();
      exitPaintMode();
    }
  };
  // Capture phase so we beat panel/modal listeners; matches the iframe-side
  // capture pattern used by the rest of the editor shortcuts.
  window.addEventListener('keydown', _escListener, true);
}

function _detachEscListener() {
  if (!_escListener || typeof window === 'undefined') return;
  window.removeEventListener('keydown', _escListener, true);
  _escListener = null;
}

function _setBodyPaintCursor(on) {
  if (typeof document === 'undefined') return;
  const body = document.body;
  if (!body) return;
  if (on) body.classList.add('pb-paint-mode');
  else body.classList.remove('pb-paint-mode');
}

/** Test-only reset. */
export function _resetForTests() {
  _state = null;
  _detachEscListener();
  _setBodyPaintCursor(false);
}
